import { SupportedDataType, UnknownSpec } from '@/core';
import {
  DataQueryPlugin,
  DataQueryPluginV2,
  EventQueryPlugin,
  QueryPluginContext,
  QueryPluginGetDataBasicResponse,
  QueryPluginGetDataProgressiveResponse,
  QueryPluginGetDataResponse,
  VariablePlugin,
} from '../../model';
import { DataPluginQueryMethod } from '../data-querying';
import { DataQuery } from './DataQuery';
import { isProgressiveDataQueryPlugin, ProgressiveDataQuery } from './ProgressiveDataQuery';

type QueryPlugin<Spec = UnknownSpec> =
  | DataQuery<Spec>
  | DataQueryPlugin<Spec>
  | VariablePlugin<Spec>
  | EventQueryPlugin<Spec>;

interface GetDataFunctionArgs {
  queryMethod: DataPluginQueryMethod;
  signal?: AbortSignal;
}

/**
 * Type signature for `getData` functions.
 */
type GetDataFn<ResponseType extends QueryPluginGetDataResponse = QueryPluginGetDataResponse> = (
  spec: UnknownSpec,
  ctx: QueryPluginContext
) => Promise<ResponseType>;

// These type signatures ensure that the right type is returned based on if the passed in plugin is a
// progressive query plugin or not.
export function getDataFunction<T extends SupportedDataType = SupportedDataType>(
  plugin: ProgressiveDataQuery,
  args: GetDataFunctionArgs
): GetDataFn<QueryPluginGetDataProgressiveResponse<T>>;

export function getDataFunction<T extends SupportedDataType = SupportedDataType>(
  plugin: DataQueryPlugin | DataQuery,
  args: GetDataFunctionArgs
): GetDataFn<QueryPluginGetDataBasicResponse<T>>;

/**
 * Retrieves the getData function from the plugin.
 * If the plugin is a class (an instance of DataQuery abstract class), it sets the abort signal.
 */
export function getDataFunction<T extends SupportedDataType = SupportedDataType>(
  plugin: DataQueryPlugin | DataQuery | ProgressiveDataQuery,
  { queryMethod, signal }: GetDataFunctionArgs
): GetDataFn<QueryPluginGetDataProgressiveResponse<T>> | GetDataFn<QueryPluginGetDataBasicResponse<T>> {
  // Fall back to getData method if getComparableData is not available on the plugin.
  const fallbackQueryMethod: DataPluginQueryMethod = 'getData';

  if (isProgressiveDataQueryPlugin(plugin)) {
    // We do not worry about setting the signal on progressive data query plugins
    // because they manage their cancellation internally with their own abort controller.
    return (plugin[queryMethod] || plugin[fallbackQueryMethod]) as GetDataFn<QueryPluginGetDataProgressiveResponse<T>>;
  }

  // Basic class-based query plugin
  if (plugin instanceof DataQuery) {
    if (signal) {
      plugin.setSignal(signal);
    }
  }

  // Basic plugins (both object and class based)
  return (plugin[queryMethod] || plugin[fallbackQueryMethod]) as GetDataFn<QueryPluginGetDataBasicResponse<T>>;
}

/**
 * Retrieves the dependsOn function from the plugin.
 */
export function getDependsOnFunction<Spec = UnknownSpec>(plugin: QueryPlugin<Spec>) {
  if (plugin instanceof DataQuery) {
    return plugin.dependsOn;
  }
  return plugin.dependsOn;
}

/**
 * Retrieves the interpolateVariables function from the plugin.
 */
export function getInterpolateVariablesFunction<Spec = UnknownSpec>(plugin: QueryPlugin<Spec>) {
  if (plugin instanceof DataQuery) {
    return plugin.interpolateVariables;
  }
  return plugin.interpolateVariables;
}

/**
 * Retrieves the injectGlobalFilters function from the plugin.
 */
export function getInjectGlobalFiltersFunction<Spec = UnknownSpec>(plugin: QueryPlugin<Spec>) {
  if (plugin instanceof DataQuery) {
    return plugin.injectGlobalFilters;
  }
  return plugin.injectGlobalFilters;
}

/**
 * Retrieves the injectVariable function from the plugin if it exists.
 */
export function getInjectVariableFunction<Spec = UnknownSpec>(plugin: QueryPlugin<Spec>) {
  if ('injectVariable' in plugin) {
    return plugin.injectVariable;
  }
  return undefined;
}

/**
 * Retrieves the extractVariableLabels function from the plugin if it exists.
 */
export function getExtractVariableLabelsFunction<Spec = UnknownSpec>(plugin: QueryPlugin<Spec>) {
  if ('extractVariableLabels' in plugin) {
    return plugin.extractVariableLabels;
  }
  return undefined;
}

/**
 * Retrieves the getVariableOpportunities function from the plugin if it exists.
 */
export function getVariableOpportunitiesFunction<Spec = UnknownSpec>(plugin: QueryPlugin<Spec>) {
  if ('getVariableOpportunities' in plugin) {
    return plugin.getVariableOpportunities;
  }
  return undefined;
}

export type InitializedDataQueryPlugin<Spec = UnknownSpec> = DataQuery<Spec> | DataQueryPlugin<Spec>;

/**
 * If the data query plugin is a class (v2), it creates an instance; otherwise, it returns the data query plugin itself.
 */
export function initializeDataQueryPlugin(plugin: DataQueryPlugin | DataQueryPluginV2): InitializedDataQueryPlugin {
  if (isDataQueryPluginV2(plugin)) {
    return new plugin.plugin();
  }

  return plugin;
}

/**
 * Returns true if data query plugin is v2
 */
export function isDataQueryPluginV2<Spec>(
  plugin: DataQueryPlugin<Spec> | DataQueryPluginV2<Spec>
): plugin is DataQueryPluginV2<Spec> {
  return (plugin as DataQueryPluginV2).plugin !== undefined;
}
